import React from "react";
import styles from "./styles.module.css";

/* One way of drawing a person across the whole panel.
 *
 * When there is a photo, it is the photo on a plain surface — a tinted plate
 * behind a transparent PNG is how avatars end up sitting on a colour nobody
 * chose.
 *
 * When there is no photo, the initials get a colour derived from the person's
 * uuid, so the same colleague is the same colour in the roster, on a task card
 * and in a chat thread. It is a recognition aid, not decoration: an
 * organization where nobody has uploaded a photo is otherwise a wall of
 * identical grey circles. Saturation and lightness are fixed so every hue
 * carries white text and holds up in both themes; only the hue varies.
 *
 * Someone invited by phone has not signed in yet and has no name at all, so the
 * fallback walks down: initials, then the last digits of their number, then a
 * question mark. It never renders empty. */
const hueFor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 360;
  }
  return hash;
};

export const userInitials = (user) => {
  const first = user?.first_name?.trim()?.[0];
  const last = user?.last_name?.trim()?.[0];

  if (first || last) return `${first || ""}${last || ""}`;

  /* Some callers only have a display string — a picker option, a chat room
   * title — rather than the two name fields. */
  const words = user?.name?.trim()?.split(/\s+/).filter(Boolean) || [];
  if (words.length) {
    return words
      .slice(0, 2)
      .map((word) => word[0])
      .join("");
  }

  const mobile = user?.mobile ? String(user.mobile) : "";
  return mobile ? mobile.slice(-2) : "؟";
};

const UserAvatar = ({ user, size = 40, className = "" }) => {
  const src = user?.avatar?.original_url;

  return (
    <span
      className={`${styles.avatar} ${src ? styles.hasImage : ""} ${className}`}
      style={{
        "--identity": `hsl(${hueFor(user?.uuid || String(user?.id || ""))} 58% 45%)`,
        width: size,
        height: size,
        fontSize: Math.max(10, Math.round(size / 2.6)),
      }}
      title={
        `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
        user?.name ||
        undefined
      }
    >
      {src ? (
        <img src={src} alt="" />
      ) : (
        <span className={styles.initials}>{userInitials(user)}</span>
      )}
    </span>
  );
};

export default UserAvatar;
